import React from 'react';
import axios from 'axios';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import { MdClose, MdAdd, MdDelete } from 'react-icons/md';
import { BiSolidCheckCircle, BiErrorCircle, BiMinusCircle } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { useRooftop, useUser } from '@store';
import { validatePhone } from '@services/validationService';
import { useGetAllLocationsQuery } from '../../locations/queries/getAllLocations.query.generated';
import { useCreateDeliveryOrdersMutation } from '../queries/createDeliveryOrder.mutation.generated';
import { useUpdateLocationNameMutation } from '../queries/updateLocationName.mutation.generated';
import { Locations } from '@gql/base-types';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

interface ManualOrderEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When set, pre-fills (and the caller typically locks) the destination — used
   *  by the run builder's per-destination "quick-add". */
  prefill?: Partial<typeof INITIAL_DESTINATION> | null;
  /** When provided, receives the created order rows (insert payloads + ids) so
   *  the caller can place them directly onto a run destination. */
  onCreated?: (orders: any[]) => void;
}

// "Destination" fields are shared by every order dropped at this stop.
const INITIAL_DESTINATION = {
  recipient_business: '',
  recipient_name: '',
  recipient_phone: '',
  delivery_address: '',
  delivery_lat: null as number | null,
  delivery_lng: null as number | null,
  special_instructions: '',
  has_return_pickup: false,
  return_description: '',
};

// One line item on the PO → one delivery_orders row (all rows share order_number).
const INITIAL_ORDER = {
  part_number: '',
  quantity: 1,
  billing_term: 'on_account',
  priority: 'normal',
  item_description: '',
};

type OrderRow = typeof INITIAL_ORDER;

/** True when any field differs from its blank value. Compared as strings, since inputs hand back text. */
const differs = <T extends object>(value: T, initial: T): boolean =>
  (Object.keys(initial) as (keyof T)[]).some(k => String(value[k]) !== String(initial[k]));

/** Formats a phone number progressively as the user types */
const formatPhoneProgressive = (value: string): string => {
  const raw = value.replace(/\D/g, '');
  const digits = raw.length > 10 && raw.startsWith('1') ? raw.slice(1) : raw;
  const len = digits.length;
  if (len === 0) return '';
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant='overline'
    sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, mb: 0.5 }}
  >
    {children}
  </Typography>
);

const ManualOrderEntryDialog: React.FC<ManualOrderEntryDialogProps> = ({
  open,
  onClose,
  onSuccess,
  prefill,
  onCreated,
}) => {
  const theme = useTheme();
  const rooftop = useRooftop();
  const user = useUser();
  const customerId = rooftop?.id || 0;
  const userEmail = user?.profile?.email || 'unknown';

  const [createOrders, { loading }] = useCreateDeliveryOrdersMutation();

  // Saved/favorite destinations for this rooftop — fed to the address picker so
  // the manager can pick a known body shop (favorites sort first).
  const { data: locationsData, refetch: refetchLocations } = useGetAllLocationsQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });
  const savedLocations = (locationsData?.locations ?? []) as (Locations | null)[];
  const [updateLocationName] = useUpdateLocationNameMutation();

  const [destination, setDestination] = React.useState({ ...INITIAL_DESTINATION });
  // One PO number shared by all line items (each becomes a delivery_orders row).
  const [poNumber, setPoNumber] = React.useState('');
  const [orders, setOrders] = React.useState<OrderRow[]>([{ ...INITIAL_ORDER }]);
  // Controlled value for the address finder (separate from the denormalized
  // address/lat/lng we persist on each order).
  const [location, setLocation] = React.useState<Locations | null>(null);
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  /** SMS-capability verdict from verifyMobilePhone: true/false once checked, null when unknown. */
  const [isMobilePhone, setIsMobilePhone] = React.useState<boolean | null>(null);
  const [phoneVerifying, setPhoneVerifying] = React.useState(false);

  // An app update waits while the open dialog holds a draft PO or the create is running.
  // The draft survives a close, so a closed dialog holds nothing. A quick-add destination
  // is filled in by the dialog itself and doesn't count as a draft.
  const hasDraft =
    poNumber.trim() !== '' ||
    orders.length !== 1 ||
    differs(orders[0], INITIAL_ORDER) ||
    (!prefill && differs(destination, INITIAL_DESTINATION));
  useReloadGuard((open && hasDraft) || loading, 'New parts order');

  const updateDestination = (field: keyof typeof INITIAL_DESTINATION) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setDestination(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhoneProgressive(event.target.value);
    setDestination(prev => ({ ...prev, recipient_phone: masked }));
    if (phoneError) setPhoneError(null);
    setIsMobilePhone(null); // stale verdict — the number changed
  };

  // Phone is required and must be a valid US number.
  const checkPhone = (value: string): boolean => {
    const valid = validatePhone(value);
    setPhoneError(valid ? null : 'Enter a valid 10-digit phone number');
    return valid;
  };

  // On blur: regex first, then the shared verifyMobilePhone endpoint (same
  // carrier/SMS-capability lookup mobile service uses). Advisory only — the
  // verdict renders as an adornment icon and never blocks submit, since a
  // landline is still a valid contact number for a delivery.
  const handlePhoneBlur = async () => {
    const value = destination.recipient_phone;
    if (!value.trim()) {
      setIsMobilePhone(null);
      return;
    }
    if (!checkPhone(value)) {
      setIsMobilePhone(null);
      return;
    }
    setPhoneVerifying(true);
    try {
      const userToken = await user?.getToken();
      const res = await axios({
        method: 'POST',
        url: '/.netlify/functions/verifyMobilePhone',
        data: { phone: value },
        headers: { authorization: `Bearer ${userToken}` },
      });
      if (res?.data?.success && (res?.data?.isMobile === true || res?.data?.isMobile === false)) {
        setIsMobilePhone(res.data.isMobile);
      } else {
        setIsMobilePhone(null);
      }
    } catch (err) {
      console.error('Failed to verify mobile phone:', err);
      setIsMobilePhone(null);
    } finally {
      setPhoneVerifying(false);
    }
  };

  const updateOrder = (index: number, field: keyof OrderRow) => (event: any) => {
    const value = event.target.value;
    setOrders(prev => prev.map((o, i) => (i === index ? { ...o, [field]: value } : o)));
  };

  const addOrder = () => setOrders(prev => [...prev, { ...INITIAL_ORDER }]);

  const removeOrder = (index: number) =>
    setOrders(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

  // Picking a known location by its business name — fill the whole destination.
  const applySavedLocation = (loc: Locations) => {
    setLocation(loc);
    if ((loc as any).phone) setIsMobilePhone(null); // stale verdict — the number changed
    setDestination(prev => ({
      ...prev,
      recipient_business: loc.name || loc.nickname || prev.recipient_business,
      recipient_phone: (loc as any).phone ? formatPhoneProgressive((loc as any).phone) : prev.recipient_phone,
      delivery_address: loc.address || '',
      delivery_lat: loc.latitude != null ? Number(loc.latitude) : null,
      delivery_lng: loc.longitude != null ? Number(loc.longitude) : null,
    }));
  };

  const handleLocationChange = (loc?: Locations | null) => {
    setLocation(loc || null);
    // Prefill business/phone ONLY from an existing saved location (a favorite
    // with a curated name). Never from a freshly geocoded address — its "name"
    // is just the street, which would clobber the business the user typed.
    const saved = !!loc?.id && savedLocations.some(l => l?.id === loc.id);
    if (saved && (loc as any)?.phone) setIsMobilePhone(null); // stale verdict — the number changed
    setDestination(prev => ({
      ...prev,
      delivery_address: loc?.address ?? '',
      delivery_lat: loc?.latitude != null ? Number(loc.latitude) : null,
      delivery_lng: loc?.longitude != null ? Number(loc.longitude) : null,
      recipient_business: saved ? (loc?.name || loc?.nickname || prev.recipient_business) : prev.recipient_business,
      recipient_phone: saved && (loc as any)?.phone ? formatPhoneProgressive((loc as any).phone) : prev.recipient_phone,
    }));
  };

  const resetForm = () => {
    setDestination({ ...INITIAL_DESTINATION });
    setPoNumber('');
    setOrders([{ ...INITIAL_ORDER }]);
    setLocation(null);
    setPhoneError(null);
    setIsMobilePhone(null);
  };

  // Seed the destination from a prefill (per-destination quick-add) when opened.
  React.useEffect(() => {
    if (!open) return;
    if (prefill) {
      setDestination({
        ...INITIAL_DESTINATION,
        ...prefill,
        recipient_phone: prefill.recipient_phone ? formatPhoneProgressive(prefill.recipient_phone) : '',
      });
      setPhoneError(null);
      setIsMobilePhone(null);
      setPoNumber('');
      setOrders([{ ...INITIAL_ORDER }]);
      setLocation(
        prefill.delivery_address
          ? ({
              address: prefill.delivery_address,
              latitude: prefill.delivery_lat ?? null,
              longitude: prefill.delivery_lng ?? null,
            } as any)
          : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (keepOpen: boolean) => {
    // ── Destination (stop-level) validation ──
    if (!destination.recipient_business.trim()) {
      toast.error('Recipient business is required');
      return;
    }
    if (!destination.recipient_name.trim()) {
      toast.error('Contact person is required');
      return;
    }
    if (!destination.recipient_phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!checkPhone(destination.recipient_phone)) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    if (!destination.delivery_address.trim()) {
      toast.error('Delivery address is required');
      return;
    }
    // ── PO + line-item validation ──
    if (!poNumber.trim()) {
      toast.error('Order / PO number is required');
      return;
    }
    if (orders.length === 0) {
      toast.error('Add at least one line item');
      return;
    }

    // Shared destination fields copied onto every order row.
    const shared = {
      customer_id: customerId,
      recipient_business: destination.recipient_business.trim(),
      recipient_name: destination.recipient_name.trim(),
      recipient_phone: destination.recipient_phone.trim(),
      delivery_address: destination.delivery_address.trim(),
      delivery_lat: destination.delivery_lat,
      delivery_lng: destination.delivery_lng,
      special_instructions: destination.special_instructions.trim() || null,
      has_return_pickup: destination.has_return_pickup,
      return_description: destination.has_return_pickup
        ? destination.return_description.trim() || null
        : null,
      status: 'pending',
      created_by: userEmail,
      updated_by: userEmail,
    };

    const objects = orders.map(o => ({
      ...shared,
      order_number: poNumber.trim(),
      part_number: o.part_number.trim() || null,
      quantity: Number(o.quantity) || 1,
      billing_term: o.billing_term,
      priority: o.priority,
      item_description: o.item_description.trim() || null,
    }));

    try {
      const result = await createOrders({ variables: { orders: objects } });
      const count = result.data?.insert_delivery_orders?.affected_rows ?? objects.length;
      toast.success(
        `${poNumber.trim()}: ${count} line item${count === 1 ? '' : 's'} created for ${destination.recipient_business.trim()}`,
      );
      // Name the saved location after the recipient business (+ carry the phone)
      // so it shows as the body shop going forward, then refresh the favorites.
      const business = destination.recipient_business.trim();
      if (location?.id && business) {
        try {
          await updateLocationName({
            variables: { id: location.id, name: business, phone: destination.recipient_phone.trim() || null },
          });
          refetchLocations();
        } catch (err) {
          console.error('Failed to name location:', err);
        }
      }
      // Hand the created rows (insert payload + new id) back so the run builder
      // can drop them straight onto the target destination.
      if (onCreated) {
        const returned = result.data?.insert_delivery_orders?.returning ?? [];
        // Match by index — all line items share the PO's order_number, so it
        // can't disambiguate them. insert_delivery_orders returns rows in input
        // order.
        const rows = objects
          .map((obj, i) => {
            const match = returned[i] ?? returned.find((r: any) => r.order_number === obj.order_number);
            return match?.id != null ? { ...obj, id: match.id } : null;
          })
          .filter(Boolean);
        if (rows.length) onCreated(rows as any[]);
      }
      onSuccess();
      if (keepOpen) {
        resetForm();
      } else {
        resetForm();
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to create orders:', error);
      toast.error(`Failed to create orders: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Manual Order Entry
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Create a PO with one or more line items for a single destination
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size='small' disabled={loading}>
            <MdClose />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Destination (this is the stop) ── */}
        <SectionTitle>Destination — this is the stop</SectionTitle>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              freeSolo
              options={(savedLocations.filter(Boolean) as Locations[])}
              getOptionLabel={o => (typeof o === 'string' ? o : o.name || o.nickname || '')}
              inputValue={destination.recipient_business}
              onInputChange={(_e, val, reason) => {
                if (reason !== 'reset') setDestination(prev => ({ ...prev, recipient_business: val }));
              }}
              onChange={(_e, val) => {
                if (val && typeof val !== 'string') applySavedLocation(val);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>{o.name || o.nickname}</Typography>
                    {o.address ? <Typography variant='caption' color='text.secondary'>{o.address}</Typography> : null}
                  </Box>
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth size='small'
                  label='Recipient Business'
                  required
                  placeholder='Search a known shop or type a name'
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <MovePlannerLocationSelect
              rooftopId={customerId}
              required
              label='Delivery Address'
              placeholder='Search saved locations or an address…'
              helperText='Favorites appear first — or search for a new address'
              location={location}
              locations={savedLocations}
              nameOverride={destination.recipient_business.trim() || undefined}
              displayValue='address'
              onLocationChange={handleLocationChange}
              onLocationFavorite={() => { refetchLocations(); }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth size='small'
              label='Contact Person'
              value={destination.recipient_name}
              onChange={updateDestination('recipient_name')}
              required
              placeholder='John Smith'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth size='small'
              label='Phone Number'
              value={destination.recipient_phone}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              error={Boolean(phoneError)}
              helperText={phoneError}
              required
              type='tel'
              placeholder='(555) 123-4567'
              slotProps={{
                htmlInput: { maxLength: 14 },
                input: {
                  endAdornment: destination.recipient_phone.trim() ? (
                    <InputAdornment position='end'>
                      {phoneVerifying ? (
                        <Tooltip title='Validating...'>
                          <span style={{ display: 'flex' }}>
                            <CircularProgress size={18} sx={{ color: 'text.disabled' }} />
                          </span>
                        </Tooltip>
                      ) : isMobilePhone === true ? (
                        <Tooltip title='Phone is able to receive SMS.'>
                          <span>
                            <BiSolidCheckCircle size={20} style={{ color: theme?.palette?.success?.main }} />
                          </span>
                        </Tooltip>
                      ) : isMobilePhone === false ? (
                        <Tooltip title='Phone is NOT able to receive SMS!'>
                          <span>
                            <BiErrorCircle size={20} style={{ color: theme?.palette?.error?.main }} />
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title='Please enter a valid mobile phone number with SMS capability. Do NOT include the country code.'>
                          <span>
                            <BiMinusCircle size={20} style={{ color: theme?.palette?.text?.disabled }} />
                          </span>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth size='small'
              label='Special Instructions'
              value={destination.special_instructions}
              onChange={updateDestination('special_instructions')}
              multiline
              rows={2}
              placeholder='Deliver to bay 3, call upon arrival…'
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Order / PO ── */}
        <SectionTitle>Order</SectionTitle>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth size='small'
              label='Order / PO Number'
              value={poNumber}
              onChange={e => setPoNumber(e.target.value)}
              required
              placeholder='PO-2024-####'
              helperText='Shared by all line items below'
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* ── Line items on this PO (each becomes its own delivery row) ── */}
        <SectionTitle>Line items</SectionTitle>
        {orders.map((order, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                Line item {index + 1}
              </Typography>
              {orders.length > 1 && (
                <Tooltip title='Remove this line item'>
                  <IconButton size='small' onClick={() => removeOrder(index)} disabled={loading}>
                    <MdDelete />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth size='small'
                  label='Part Number'
                  value={order.part_number}
                  onChange={updateOrder(index, 'part_number')}
                  placeholder='51117205996'
                  helperText='SKU for scanning (optional)'
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth size='small'
                  label='Quantity'
                  type='number'
                  value={order.quantity}
                  onChange={updateOrder(index, 'quantity')}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size='small'>
                  <InputLabel>Billing</InputLabel>
                  <Select
                    value={order.billing_term}
                    label='Billing'
                    onChange={updateOrder(index, 'billing_term')}
                  >
                    <MenuItem value='on_account'>On Account</MenuItem>
                    <MenuItem value='cod'>COD</MenuItem>
                    <MenuItem value='net_30'>Net 30</MenuItem>
                    <MenuItem value='urgent'>Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size='small'>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={order.priority}
                    label='Priority'
                    onChange={updateOrder(index, 'priority')}
                  >
                    <MenuItem value='normal'>Normal</MenuItem>
                    <MenuItem value='rush'>Rush</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth size='small'
                  label='Item Description'
                  value={order.item_description}
                  onChange={updateOrder(index, 'item_description')}
                  multiline
                  rows={2}
                  placeholder='Fender assembly, bumper cover, headlights…'
                />
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button
          onClick={addOrder}
          disabled={loading}
          startIcon={<MdAdd />}
          sx={{ textTransform: 'none' }}
        >
          Add another line item
        </Button>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Return pickup (stop-level) ── */}
        <SectionTitle>Return pickup</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={destination.has_return_pickup}
              onChange={updateDestination('has_return_pickup')}
            />
          }
          label='This delivery includes a return pickup'
        />
        {destination.has_return_pickup && (
          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth size='small'
              label='Return Description'
              value={destination.return_description}
              onChange={updateDestination('return_description')}
              multiline
              rows={2}
              placeholder='Old bumper, core return…'
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none', mr: 'auto' }}>
          Cancel
        </Button>
        <Button
          variant='outlined'
          onClick={() => handleSubmit(true)}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Create &amp; add another
        </Button>
        <Button
          variant='contained'
          onClick={() => handleSubmit(false)}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Creating…' : 'Create PO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualOrderEntryDialog;
